// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Badge, Button, Group, Loader, Modal, Stack, Table, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconCalendarRepeat } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { AppointmentDetails } from '../../hooks/useAppointmentDetails';
import { cancelPatientAppointment } from '../../services/appointment-lifecycle-service';
import { formatAppointmentDate, formatCopay } from '../../utils/appointment-formatting';
import { ensurePatientTimezone } from '../../utils/patient-timezone';
import { AppointmentCancellationModal } from './AppointmentCancellationModal';
import { AppointmentJoinButton } from './AppointmentJoinButton';
import { AppointmentTimeWithTimezone } from './AppointmentTimeWithTimezone';

interface UpcomingAppointmentsTableProps {
  appointments: AppointmentDetails[];
  loading: boolean;
  onRefresh?: () => void;
}

function PaymentStatusCell({ details }: { details: AppointmentDetails }): JSX.Element {
  if (details.paymentStatus === 'pending') {
    return (
      <Anchor component={Link} to={`/pay/${details.appointment.id}`} size="sm" fw={600}>
        Pay Now
      </Anchor>
    );
  }
  if (details.paymentStatus === 'paid' && details.copay && details.copay > 0) {
    return <Anchor size="sm">Paid - View Receipt</Anchor>;
  }
  return <Text size="sm">n/a</Text>;
}

/**
 * Table displaying the patient's upcoming (booked, future) appointments.
 * Join opens the Zoom link. Cancel triggers cancellation with reason input.
 * @param root0 - Component props
 * @param root0.appointments - Enriched appointment details to display
 * @param root0.loading - Whether appointment data is still loading
 * @param root0.onRefresh - Callback to refresh the appointment list
 * @returns The upcoming appointments table component
 */
export function UpcomingAppointmentsTable({
  appointments,
  loading,
  onRefresh,
}: UpcomingAppointmentsTableProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const navigate = useNavigate();
  const [cancelModalOpened, cancelModalHandlers] = useDisclosure(false);
  const [cancelTarget, setCancelTarget] = useState<AppointmentDetails | undefined>();
  const [isCancelling, setIsCancelling] = useState(false);
  const [confirmationOpened, confirmationHandlers] = useDisclosure(false);

  function openCancelModal(details: AppointmentDetails): void {
    setCancelTarget(details);
    cancelModalHandlers.open();
  }

  async function handleCancel(): Promise<void> {
    if (!cancelTarget?.appointment.id) {
      return;
    }

    setIsCancelling(true);
    try {
      if (profile) {
        await ensurePatientTimezone(medplum, profile);
      }
      await cancelPatientAppointment(medplum, cancelTarget.appointment.id);

      showNotification({
        color: 'green',
        title: 'Appointment cancelled',
        message: 'Your appointment has been cancelled successfully.',
      });

      cancelModalHandlers.close();
      confirmationHandlers.close();
      onRefresh?.();
    } catch (err) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to cancel appointment',
      });
    } finally {
      setIsCancelling(false);
    }
  }

  if (loading) {
    return (
      <Stack align="center" py="md">
        <Loader size="sm" />
      </Stack>
    );
  }

  if (appointments.length === 0) {
    return (
      <Text c="dimmed" py="md">
        No upcoming appointments.
      </Text>
    );
  }

  return (
    <>
      <Table miw={1000}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Type</Table.Th>
            <Table.Th>Date</Table.Th>
            <Table.Th>Time</Table.Th>
            <Table.Th>Amount</Table.Th>
            <Table.Th>Payment Status</Table.Th>
            <Table.Th>Provider</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {appointments.map((details) => (
            <Table.Tr key={details.appointment.id}>
              <Table.Td>
                <Group gap="xs">
                  {details.appointmentType}
                  {details.appointment.status === 'arrived' && (
                    <Badge size="xs" color="green" variant="light">
                      Arrived
                    </Badge>
                  )}
                  {details.appointment.status === 'checked-in' && (
                    <Badge size="xs" color="blue" variant="light">
                      Checked In
                    </Badge>
                  )}
                  {details.paymentStatus === 'pending' && (
                    <Badge size="xs" color="orange" variant="light">
                      Payment pending
                    </Badge>
                  )}
                </Group>
              </Table.Td>
              <Table.Td>{formatAppointmentDate(details.appointment.start)}</Table.Td>
              <Table.Td>
                <AppointmentTimeWithTimezone
                  start={details.appointment.start}
                  end={details.appointment.end}
                  textProps={{ size: 'sm' }}
                />
              </Table.Td>
              <Table.Td>{formatCopay(details.copay)}</Table.Td>
              <Table.Td>
                <PaymentStatusCell details={details} />
              </Table.Td>
              <Table.Td>
                <Anchor size="sm">{details.providerName}</Anchor>
              </Table.Td>
              <Table.Td>
                <Group gap="xs" wrap="nowrap">
                  <AppointmentJoinButton joinUrl={details.joinUrl} />
                  <Button
                    size="xs"
                    variant="subtle"
                    leftSection={<IconCalendarRepeat size={14} />}
                    onClick={() => navigate(`/appointments/reschedule/${details.appointment.id}`)}
                  >
                    Reschedule
                  </Button>
                  <Button size="xs" variant="outline" onClick={() => openCancelModal(details)}>
                    Cancel
                  </Button>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal
        opened={cancelModalOpened}
        onClose={cancelModalHandlers.close}
        overlayProps={{ opacity: confirmationOpened ? 0 : 0.55 }}
        centered
        withCloseButton={false}
        padding="xl"
        radius="lg"
        size="lg"
      >
        {cancelTarget && (
          <Stack gap="lg" align="center">
            <Title order={2}>Reschedule or Cancel</Title>

            <Stack gap={4} align="center">
              <Text fw={600} size="md">
                {cancelTarget.appointmentType}
              </Text>
              <Text fw={600} size="lg">
                {formatAppointmentDate(cancelTarget.appointment.start)}
              </Text>
              <AppointmentTimeWithTimezone
                start={cancelTarget.appointment.start}
                end={cancelTarget.appointment.end}
                groupProps={{ justify: 'center' }}
                textProps={{ fw: 600, size: 'lg' }}
              />
            </Stack>

            <Text size="sm" c="dimmed" ta="center">
              Would you like to reschedule or cancel this appointment?
            </Text>

            <Group justify="center" w="100%">
              <Button
                variant="outline"
                onClick={() => {
                  cancelModalHandlers.close();
                  navigate(`/appointments/reschedule/${cancelTarget.appointment.id}`);
                }}
              >
                Reschedule
              </Button>
              <Button color="red" onClick={confirmationHandlers.open}>
                Cancel appointment
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      <AppointmentCancellationModal
        opened={confirmationOpened}
        onClose={confirmationHandlers.close}
        onConfirm={handleCancel}
        loading={isCancelling}
      />
    </>
  );
}
